/**
 * HeaderWash — mirror of mobile components/ui/HeaderWash.tsx.
 *
 * The subtle emerald-to-transparent wash that sits behind the top of a screen.
 * It intentionally uses the LIGHT-mode primary (#0F8A63) in both schemes
 * (dark mode's `primary` is the brighter mint, which would read as a louder
 * header on OLED black). Pass `color` if a screen genuinely wants a different
 * wash. The parent must be `position: relative` (Tailwind `relative`).
 */

import type { CSSProperties } from 'react'
import { Colors } from '@/theme/tokens'
import { useColorScheme } from '@/hooks/use-color-scheme'
import { withAlpha } from '@/theme/color'

/** Default wash height — matches the `topGradient` style mobile screens declared. */
export const HEADER_WASH_HEIGHT = 200

const WASH_ALPHA_LIGHT = 0.1
const WASH_ALPHA_DARK = 0.18

interface HeaderWashProps {
  /** Height of the wash. Ignored when `style` sets its own height. */
  height?: number
  /** Merged over the default absolute positioning. */
  style?: CSSProperties
  className?: string
  /** Override the tint (hex). Defaults to the brand emerald. */
  color?: string
  /**
   * Web: on md+ the wash bleeds past the 600px content column to the edges of
   * the shell's <main> (which clips it), so wide screens get one seamless wash
   * instead of a column-shaped band. Pass false outside the shell.
   */
  bleed?: boolean
}

export function HeaderWash({ height = HEADER_WASH_HEIGHT, style, className, color, bleed = true }: HeaderWashProps) {
  const scheme = useColorScheme()
  const base = color ?? Colors.light.primary
  const tint = withAlpha(base, scheme === 'dark' ? WASH_ALPHA_DARK : WASH_ALPHA_LIGHT)

  return (
    <div
      aria-hidden
      className={[
        'absolute inset-x-0 top-0',
        bleed ? 'md:left-[-100vw] md:right-[-100vw]' : '',
        className ?? '',
      ].join(' ')}
      style={{
        height,
        zIndex: 0,
        pointerEvents: 'none',
        background: `linear-gradient(to bottom, ${tint}, transparent)`,
        ...style,
      }}
    />
  )
}

export default HeaderWash
